import type { CallRecordListItem } from '@techgitter/contracts';

/** A call's start time as "14 Oct, 09:12" in Sydney time; `—` when absent. */
export const formatCallTime = (value: string | null): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Australia/Sydney',
    });
  } catch {
    return value;
  }
};

/**
 * What a call's line in a log should say. The operational summary when there
 * is one; otherwise a plain statement of what kind of call it was. CDR-only
 * rows and missed calls have no recording, so no summary, and a blank line
 * would read as a fault rather than as "there was nothing to summarise".
 */
export const callLogText = (
  call: Pick<CallRecordListItem, 'operationalSummaryText' | 'callKind' | 'direction'>,
): { text: string; isFallback: boolean } => {
  const summary = (call.operationalSummaryText ?? '').trim();
  if (summary) return { text: summary, isFallback: false };

  const kind =
    call.callKind === 'missed'
      ? 'Missed call'
      : call.callKind === 'unanswered'
      ? 'Unanswered call'
      : call.callKind === 'voicemail'
      ? 'Voicemail'
      : call.direction === 'inbound'
      ? 'Inbound call'
      : call.direction === 'outbound'
      ? 'Outbound call'
      : call.direction === 'internal'
      ? 'Internal call'
      : 'Call';
  return { text: `${kind} — no summary`, isFallback: true };
};
